import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subscription } from 'rxjs';
import { AiService, EmailWriterGenerateResponse } from '../../../../shared/service/ai-service';
import { ToasterCommonService } from '../../../../shared/service/toaster-common-service';
import { Editor, Toolbar } from 'ngx-editor';

/** Same as email-template-form / event-info: localhost, mailto, tel, relative paths. */
const LINK_VALIDATION_PATTERN =
  '^(https?:\\/\\/[^\\s]+|mailto:[^\\s@]+@[^\\s]+|tel:[^\\s]+|\\/[^\\s]*|\\.\\/[^\\s]*)$';

@Component({
  selector: 'app-email-generate-popup',
  standalone: false,
  templateUrl: './email-generate-popup.html',
  styleUrl: './email-generate-popup.scss',
})
export class EmailGeneratePopup implements OnInit, OnDestroy {
  form!: FormGroup;
  isGenerating = false;
  generateSubscription?: Subscription;
  editor!: Editor;
  toolbar: Toolbar = [
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['code', 'blockquote'],
    ['ordered_list', 'bullet_list'],
    [{ heading: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] }],
    ['text_color', 'background_color'],
    ['align_left', 'align_center', 'align_right', 'align_justify'],
    ['link'],
  ];

  constructor(
    public dialogRef: MatDialogRef<EmailGeneratePopup>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    private spinner: NgxSpinnerService,
    private _cdr: ChangeDetectorRef,
    private aiService: AiService,
    private toastr: ToasterCommonService
  ) {}

  ngOnInit() {
    this.editor = new Editor({
      history: true,
      keyboardShortcuts: true,
      inputRules: true,
      linkValidationPattern: LINK_VALIDATION_PATTERN,
    });

    const initialPrompt =
      'Generate a professional English email for a conference system. Return a clear email subject and body. Use placeholders like [[first_name]] if needed.';
    const initialSubjectHint = 'Conference update and reminder';
    const initialSignature = 'Conference Organizing Committee';

    this.form = new FormGroup({
      prompt: new FormControl(initialPrompt, [Validators.required]),
      tone: new FormControl('Professional'),
      audience: new FormControl(''),
      purpose: new FormControl(''),
      language: new FormControl('English'),
      subject_hint: new FormControl(initialSubjectHint),
      signature: new FormControl(initialSignature),
      variables: new FormControl([]),

      generated_subject: new FormControl(this.data?.generated_subject ?? ''),
      generated_body: new FormControl(this.data?.generated_body ?? ''),
    });
  }

  ngOnDestroy() {
    this.generateSubscription?.unsubscribe();
    this.editor?.destroy();
  }

  generateEmail() {
    const promptFromBody = String(this.form.value.generated_body ?? '').trim();
    const promptFromTemplate = String(this.form.value.prompt ?? '').trim();
    const effectivePrompt = promptFromBody || promptFromTemplate;

    if (!effectivePrompt) {
      this.form.markAllAsTouched();
      this.toastr.showError('Please add a prompt before generating.', 'Validation Error');
      return;
    }

    this.isGenerating = true;
    this.spinner.show();

    const payload = {
      text: effectivePrompt,
      tone: 'professional',
      language: 'en',
      preserve_placeholders: true,
    };

    this.generateSubscription = this.aiService.textGenerator(payload).subscribe({
      next: (resp) => {
        const extracted = this.extractGeneratedEmail(resp);
        const subject = extracted.subject ? String(extracted.subject).trim() : '';
        const body = extracted.body ? String(extracted.body).trim() : '';

        if (subject) {
          this.form.get('generated_subject')?.setValue(subject);
        }
        if (body) {
          this.form.get('generated_body')?.setValue(body);
        }
        this.isGenerating = false;
        this.spinner.hide();
        this._cdr.detectChanges();
      },
      error: () => {
        this.isGenerating = false;
        this.spinner.hide();
        this.toastr.showError('Failed to generate email. Please try again.', 'AI Error');
        this._cdr.detectChanges();
      },
    });
  }

  useGenerated() {
    const subject = String(this.form.value.generated_subject ?? '');
    const body = String(this.form.value.generated_body ?? '');
    this.dialogRef.close({ subject, body });
  }

  private extractGeneratedEmail(resp: EmailWriterGenerateResponse | string): {
    subject: string;
    body: string;
  } {
    if (typeof resp === 'string') {
      return { subject: '', body: resp };
    }

    const anyResp = resp as any;

    let subject = (anyResp.subject ??
      anyResp.subject_text ??
      anyResp.generated_subject ??
      anyResp.generatedSubject ??
      anyResp.title ??
      '') as string;

    if (
      (!subject || !String(subject).trim()) &&
      Array.isArray(anyResp.subject_lines) &&
      anyResp.subject_lines.length
    ) {
      subject = anyResp.subject_lines[0] as string;
    }

    let body = (anyResp.body ??
      anyResp.body_html ??
      anyResp.body_text ??
      anyResp.generated_body ??
      anyResp.generatedBody ??
      anyResp.content ??
      anyResp.html ??
      anyResp.text ??
      '') as string;

    if (typeof body === 'string') {
      const normalized = body.replace(/\r\n/g, '\n').trim();
      const looksLikeHtml = /<[a-z][\s\S]*>/i.test(normalized);
      if (normalized && !looksLikeHtml) {
        body = `<p>${normalized.replace(/\n/g, '<br />')}</p>`;
      } else {
        body = normalized;
      }
    } else {
      body = '';
    }

    return { subject: String(subject ?? ''), body: String(body ?? '') };
  }
}
